/**
 * CustomerOrderPortal — public page mounted at /order/:token.
 *
 * Renders an admin-built estimate (line items, prices, images) for a hospital
 * contact to review. The customer can:
 *   - Adjust each line's quantity
 *   - Fill contact + shipping form
 *   - Enter their PO number (optional)
 *   - Upload their PO PDF (optional)
 *   - Approve as guest, or create an account and approve
 *
 * On approval the order's status transitions from `estimate` -> `po_received`
 * and it shows up in the admin Orders board for fulfillment.
 *
 * Route:  /order/:token   (public — no auth)
 * API:    GET  /api/public/orders/:token
 *         POST /api/public/orders/:token/upload-po
 *         POST /api/public/orders/:token/approve
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  CheckCircle2,
  Upload,
  X,
  ShoppingCart,
  ShieldCheck,
  Building2,
  Package,
  Loader2,
  ChevronDown,
  ChevronUp,
  Minus,
  Plus,
  FileText,
  Truck,
  Clock,
  MapPin,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const money = (v) =>
  v == null || Number.isNaN(v) ? "—" : `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function CustomerOrderPortal() {
  const { token } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingPo, setUploadingPo] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [confirmData, setConfirmData] = useState(null);
  const [qtyByLine, setQtyByLine] = useState({});
  const [expanded, setExpanded] = useState({});
  const [form, setForm] = useState({
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    hospital_name: "",
    department: "",
    shipping_address: "",
    billing_address: "",
    po_number: "",
    po_pdf_url: "",
    po_pdf_original_name: "",
    notes_to_seller: "",
    create_account: false,
    password: "",
    accept_terms: false,
  });
  const fileRef = useRef(null);

  // ── Load ────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const r = await axios.get(`${API}/public/orders/${token}`);
      const o = r.data.order;
      setOrder(o);
      const qmap = {};
      (o.items || []).forEach((it) => { qmap[it.id] = it.quantity || 1; });
      setQtyByLine(qmap);
      setForm((f) => ({
        ...f,
        contact_name: o.contact_name && o.contact_name !== "—" ? o.contact_name : "",
        contact_email: o.contact_email || "",
        contact_phone: o.contact_phone || "",
        hospital_name: o.hospital_name || "",
        shipping_address: o.shipping_address || "",
      }));
    } catch (e) {
      setLoadError(e.response?.data?.detail || "This order link is invalid or has expired.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // ── Derived totals ──────────────────────────────────────────────────────
  const items = order?.items || [];
  const itemsSubtotal = items.reduce((sum, it) => {
    const q = qtyByLine[it.id] ?? it.quantity ?? 0;
    const price = it.unit_cost ?? 0;
    return sum + (price * q);
  }, 0);
  const freight = order?.freight_cost || 0;
  const grandTotal = itemsSubtotal + freight;

  // ── PO PDF upload ───────────────────────────────────────────────────────
  const handlePoUpload = async (file) => {
    if (!file) return;
    setUploadingPo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/public/orders/${token}/upload-po`, fd);
      setForm((f) => ({
        ...f,
        po_pdf_url: r.data.url,
        po_pdf_original_name: r.data.original_name || r.data.filename,
      }));
      toast.success(`Attached ${file.name}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingPo(false);
    }
  };

  // ── Submit ──────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.contact_name.trim()) return toast.error("Please enter your name");
    if (!form.contact_email.trim() || !/@/.test(form.contact_email)) return toast.error("Please enter a valid email");
    if (!form.hospital_name.trim()) return toast.error("Please enter your facility name");
    if (!form.shipping_address.trim() || form.shipping_address.length < 5) return toast.error("Please enter a shipping address");
    if (!form.accept_terms) return toast.error("Please accept the terms to place the order");
    if (form.create_account && (!form.password || form.password.length < 6)) return toast.error("Password must be at least 6 characters");

    setSubmitting(true);
    try {
      const line_items = items.map((it) => ({
        id: it.id,
        quantity: qtyByLine[it.id] ?? it.quantity ?? 1,
      }));
      const r = await axios.post(`${API}/public/orders/${token}/approve`, {
        contact_name: form.contact_name.trim(),
        contact_email: form.contact_email.trim().toLowerCase(),
        contact_phone: form.contact_phone.trim() || null,
        hospital_name: form.hospital_name.trim(),
        department: form.department.trim() || null,
        shipping_address: form.shipping_address.trim(),
        billing_address: form.billing_address.trim() || null,
        po_number: form.po_number.trim() || null,
        po_pdf_url: form.po_pdf_url || null,
        notes_to_seller: form.notes_to_seller.trim() || null,
        line_items,
        create_account: form.create_account,
        password: form.create_account ? form.password : null,
        accept_terms: true,
      });
      setConfirmed(true);
      setConfirmData(r.data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not place order — please try again");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render states ──────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="max-w-md text-center bg-white border border-slate-200 rounded-xl p-8 shadow-sm">
          <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
            <X className="w-7 h-7 text-red-500" />
          </div>
          <h1 className="text-lg font-bold text-slate-900 mb-2">Order link unavailable</h1>
          <p className="text-sm text-slate-600">{loadError}</p>
          <p className="text-xs text-slate-400 mt-4">Please contact Sterile Mate at 800.988.3892 if you believe this is an error.</p>
        </div>
      </div>
    );
  }

  if (confirmed) {
    return <ConfirmationScreen data={confirmData} order={order} />;
  }

  const approvable = order.approvable !== false;

  // Once the customer has approved (or the order has moved past the estimate
  // stage), the same /order/{token} link becomes a read-only tracking page.
  if (!approvable) {
    return <OrderTrackingView order={order} />;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* HEADER */}
      <header className="bg-[#0f2141] text-white">
        <div className="max-w-5xl mx-auto px-4 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center">
              <ShoppingCart className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-bold">Sterile Mate · AskIsaac</div>
              <div className="text-sm font-semibold">Customer Order Portal</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-slate-400">Estimate #</div>
            <div className="text-sm font-mono font-bold text-white" data-testid="portal-order-id">{order.id}</div>
          </div>
        </div>
      </header>

      {!approvable && (
        <div className="bg-amber-50 border-y border-amber-200 text-amber-800">
          <div className="max-w-5xl mx-auto px-4 py-3 text-sm">
            This order has already been submitted &mdash; the review form is now read-only.
          </div>
        </div>
      )}

      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Hero */}
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">
            Review your estimate, {(order.contact_name && order.contact_name !== "—" ? order.contact_name.split(" ")[0] : "there")}.
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Adjust quantities if needed, add your PO number, and place the order.
            Everything below has been priced and reserved for <strong>{order.hospital_name}</strong>.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* LEFT — Line items */}
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <Package className="w-5 h-5 text-slate-500" />
                <h2 className="font-semibold text-slate-900">Line items ({items.length})</h2>
              </div>
              <div className="space-y-3">
                {items.map((it) => (
                  <LineItemCard
                    key={it.id}
                    item={it}
                    qty={qtyByLine[it.id] ?? it.quantity}
                    onQty={(q) => setQtyByLine({ ...qtyByLine, [it.id]: q })}
                    expanded={!!expanded[it.id]}
                    onToggle={() => setExpanded({ ...expanded, [it.id]: !expanded[it.id] })}
                    disabled={!approvable}
                  />
                ))}
              </div>
            </div>

            {/* Contact + shipping */}
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <Building2 className="w-5 h-5 text-slate-500" />
                <h2 className="font-semibold text-slate-900">Contact &amp; shipping</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <TextField label="Your name *" value={form.contact_name} onChange={(v) => setForm({ ...form, contact_name: v })} placeholder="Jane Smith" testid="portal-name" />
                <TextField label="Email *" type="email" value={form.contact_email} onChange={(v) => setForm({ ...form, contact_email: v })} placeholder="jane@hospital.com" testid="portal-email" />
                <TextField label="Phone" value={form.contact_phone} onChange={(v) => setForm({ ...form, contact_phone: v })} placeholder="713-555-0123" testid="portal-phone" />
                <TextField label="Facility / hospital *" value={form.hospital_name} onChange={(v) => setForm({ ...form, hospital_name: v })} placeholder="Memorial Hospital" testid="portal-hospital" />
                <TextField label="Department" value={form.department} onChange={(v) => setForm({ ...form, department: v })} placeholder="Biomed / SPD / OR" testid="portal-dept" />
              </div>
              <div className="mt-3">
                <TextArea label="Shipping address *" value={form.shipping_address} onChange={(v) => setForm({ ...form, shipping_address: v })} placeholder="1234 Main St, Houston, TX 77002" rows={2} testid="portal-ship-addr" />
              </div>
              <div className="mt-3">
                <TextArea label="Billing address (if different)" value={form.billing_address} onChange={(v) => setForm({ ...form, billing_address: v })} rows={2} testid="portal-bill-addr" />
              </div>
            </div>

            {/* PO */}
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <FileText className="w-5 h-5 text-slate-500" />
                <h2 className="font-semibold text-slate-900">Purchase Order (optional)</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <TextField label="PO Number" value={form.po_number} onChange={(v) => setForm({ ...form, po_number: v })} placeholder="e.g., PO-2026-8891" testid="portal-po-number" />
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">PO PDF (optional)</label>
                  {form.po_pdf_url ? (
                    <div className="flex items-center gap-2 p-2 bg-emerald-50 border border-emerald-200 rounded">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      <span className="text-xs text-slate-700 flex-1 truncate">{form.po_pdf_original_name}</span>
                      <button type="button" onClick={() => setForm({ ...form, po_pdf_url: "", po_pdf_original_name: "" })} className="text-slate-400 hover:text-red-600" data-testid="portal-clear-po-pdf">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <div
                      className="border-2 border-dashed border-slate-300 rounded p-3 text-center text-xs text-slate-500 hover:border-emerald-400 hover:bg-emerald-50/30 transition-colors cursor-pointer"
                      onClick={() => fileRef.current?.click()}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => { e.preventDefault(); handlePoUpload(e.dataTransfer.files?.[0]); }}
                      data-testid="portal-po-dropzone"
                    >
                      <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx" className="hidden" onChange={(e) => handlePoUpload(e.target.files?.[0])} data-testid="portal-po-input" />
                      <Upload className="w-4 h-4 inline mr-1" />
                      {uploadingPo ? "Uploading…" : "Click or drag PO PDF"}
                    </div>
                  )}
                </div>
              </div>
              <TextArea label="Notes to Sterile Mate (optional)" value={form.notes_to_seller} onChange={(v) => setForm({ ...form, notes_to_seller: v })} placeholder="Delivery instructions, dock hours, etc." rows={2} className="mt-3" testid="portal-notes" />
            </div>
          </div>

          {/* RIGHT — Order summary */}
          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-6 space-y-4">
              <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
                <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Order summary</h3>
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between"><dt className="text-slate-600">Line items ({items.length})</dt><dd className="font-semibold text-slate-900">{money(itemsSubtotal)}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-600">Freight</dt><dd className="font-semibold text-slate-900">{freight ? money(freight) : "TBD"}</dd></div>
                  <div className="border-t border-slate-200 pt-2 flex justify-between text-base">
                    <dt className="font-bold text-slate-900">Total (est.)</dt>
                    <dd className="font-bold text-slate-900" data-testid="portal-total">{money(grandTotal)}</dd>
                  </div>
                </dl>
                {order.payment_terms && (
                  <div className="mt-3 text-[11px] text-slate-500">Payment terms: <strong>{order.payment_terms}</strong></div>
                )}
              </div>

              {/* Account creation toggle */}
              <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.create_account}
                    onChange={(e) => setForm({ ...form, create_account: e.target.checked })}
                    className="mt-0.5"
                    data-testid="portal-create-account"
                  />
                  <div>
                    <div className="text-sm font-semibold text-slate-900">Create an account</div>
                    <div className="text-[11px] text-slate-500">Track future orders, reuse this info. Otherwise you&apos;ll check out as a guest.</div>
                  </div>
                </label>
                {form.create_account && (
                  <div className="mt-3">
                    <TextField label="Password (min 6 chars)" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} placeholder="••••••••" testid="portal-password" />
                  </div>
                )}
              </div>

              {/* Terms + submit */}
              <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
                <label className="flex items-start gap-2 cursor-pointer mb-4">
                  <input
                    type="checkbox"
                    checked={form.accept_terms}
                    onChange={(e) => setForm({ ...form, accept_terms: e.target.checked })}
                    className="mt-0.5"
                    data-testid="portal-terms"
                  />
                  <div className="text-xs text-slate-600 leading-relaxed">
                    I confirm the quantities above and authorize Sterile Mate LLC to fulfill this order under the payment terms shown. Final invoice will match approved quantities + shipping.
                  </div>
                </label>
                <button
                  type="submit"
                  disabled={submitting || !approvable}
                  className="w-full bg-[#0f2141] hover:bg-[#1a2f5c] disabled:bg-slate-400 disabled:cursor-not-allowed text-white font-bold py-3 rounded-lg flex items-center justify-center gap-2 transition-colors"
                  data-testid="portal-submit"
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Placing order…</>
                  ) : (
                    <><ShieldCheck className="w-4 h-4" /> Place order &amp; send PO</>
                  )}
                </button>
                <p className="text-[10px] text-slate-400 text-center mt-2">
                  Sterile Mate LLC · SAM.gov CAGE 9GH66 · <a href="mailto:george.broughton@sterilemate.com" className="text-slate-500 hover:underline">contact us</a>
                </p>
              </div>
            </div>
          </div>
        </form>
      </main>

      <footer className="border-t border-slate-200 bg-white mt-8">
        <div className="max-w-5xl mx-auto px-4 py-4 text-center text-[11px] text-slate-500">
          © 2026 Sterile Mate LLC · AskIsaac Inc · <a href="https://sterilemate.com" className="hover:underline">sterilemate.com</a>
        </div>
      </footer>
    </div>
  );
}


// ── Sub-components ─────────────────────────────────────────────────────────
function LineItemCard({ item, qty, onQty, expanded, onToggle, disabled }) {
  const price = item.unit_cost ?? 0;
  const lineTotal = price * (qty || 0);
  return (
    <div className="border border-slate-200 rounded-lg p-4 hover:border-slate-300 transition-colors">
      <div className="flex gap-4">
        {item.image_url ? (
          <img src={item.image_url} alt="" className="w-20 h-20 rounded object-contain bg-slate-50 flex-shrink-0" />
        ) : (
          <div className="w-20 h-20 rounded bg-slate-100 flex items-center justify-center flex-shrink-0">
            <Package className="w-6 h-6 text-slate-400" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="font-semibold text-slate-900 text-sm truncate">{item.name}</div>
              {item.sku && <div className="text-[11px] text-slate-500 font-mono">SKU: {item.sku}</div>}
              {item.description && !expanded && (
                <div className="text-xs text-slate-600 mt-1 line-clamp-1">{item.description}</div>
              )}
            </div>
            <div className="text-right flex-shrink-0">
              <div className="font-bold text-slate-900" data-testid={`portal-line-price-${item.id}`}>{money(price)}</div>
              <div className="text-[10px] uppercase tracking-wide text-slate-400">per {item.uom || "each"}</div>
            </div>
          </div>

          <div className="mt-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={disabled || qty <= 1}
                onClick={() => onQty(Math.max(1, qty - 1))}
                className="w-8 h-8 border border-slate-300 rounded flex items-center justify-center hover:bg-slate-100 disabled:opacity-40"
                data-testid={`portal-qty-minus-${item.id}`}
              >
                <Minus className="w-3.5 h-3.5" />
              </button>
              <input
                type="number"
                min="1"
                disabled={disabled}
                value={qty}
                onChange={(e) => onQty(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-16 h-8 text-center border border-slate-300 rounded font-mono text-sm"
                data-testid={`portal-qty-input-${item.id}`}
              />
              <button
                type="button"
                disabled={disabled}
                onClick={() => onQty((qty || 0) + 1)}
                className="w-8 h-8 border border-slate-300 rounded flex items-center justify-center hover:bg-slate-100 disabled:opacity-40"
                data-testid={`portal-qty-plus-${item.id}`}
              >
                <Plus className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="text-sm font-semibold text-slate-900" data-testid={`portal-line-total-${item.id}`}>
              = {money(lineTotal)}
            </div>
          </div>

          {(item.description || item.category) && (
            <button type="button" onClick={onToggle} className="mt-2 text-[11px] text-slate-500 hover:text-slate-900 flex items-center gap-1">
              {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {expanded ? "Hide details" : "Show details"}
            </button>
          )}
          {expanded && (
            <div className="mt-2 pt-2 border-t border-slate-100 text-xs text-slate-600 space-y-1">
              {item.description && <div>{item.description}</div>}
              {item.category && <div><span className="text-slate-400 uppercase text-[10px] font-semibold tracking-wide">Category:</span> {item.category}</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TextField({ label, value, onChange, placeholder, type = "text", testid }) {
  return (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
        data-testid={testid}
      />
    </div>
  );
}

function TextArea({ label, value, onChange, placeholder, rows = 3, className = "", testid }) {
  return (
    <div className={className}>
      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">{label}</label>
      <textarea
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:border-slate-500 focus:outline-none resize-y"
        data-testid={testid}
      />
    </div>
  );
}

function ConfirmationScreen({ data, order }) {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-12">
      <div className="max-w-lg w-full bg-white border border-slate-200 rounded-2xl p-8 shadow-sm text-center">
        <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-5">
          <CheckCircle2 className="w-9 h-9 text-emerald-600" />
        </div>
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Order placed 🎉</h1>
        <p className="text-sm text-slate-600 mb-6">{data?.message || "Sterile Mate will begin fulfillment shortly."}</p>
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-6 text-left">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Order confirmation</div>
          <div className="font-mono text-lg font-bold text-slate-900 mt-1">{data?.order_id || order?.id}</div>
          <div className="text-xs text-slate-500 mt-2">
            A confirmation copy will arrive at your inbox. Track updates via the same link, or reply to the confirmation email for anything you need.
          </div>
        </div>
        <p className="text-[11px] text-slate-400">
          Sterile Mate LLC · 800.988.3892 · <a href="mailto:george.broughton@sterilemate.com" className="hover:underline">george.broughton@sterilemate.com</a>
        </p>
      </div>
    </div>
  );
}

// ── Read-only tracking view (shown once the customer has approved) ────────
const STATUS_STEPS = [
  { id: "po_received", label: "Order received", desc: "We got your PO and are locking in inventory." },
  { id: "in_fulfillment", label: "Fulfillment", desc: "Packing and preparing your shipment." },
  { id: "shipped", label: "Shipped", desc: "On its way to your facility." },
  { id: "delivered", label: "Delivered", desc: "Signed for at your dock." },
];

function statusIndex(status) {
  const idx = STATUS_STEPS.findIndex((s) => s.id === status);
  return idx === -1 ? 0 : idx;
}

function OrderTrackingView({ order }) {
  const currentIdx = statusIndex(order.status);
  const items = order.items || [];
  const subtotal = order.items_subtotal || 0;
  const freight = order.freight_cost || 0;
  const total = order.transaction_total || (subtotal + freight);

  const trackingLink = order.tracking_number && order.carrier
    ? carrierTrackingLink(order.carrier, order.tracking_number)
    : null;

  const isCancelled = order.status === "cancelled" || order.status === "on_hold";

  return (
    <div className="min-h-screen bg-slate-50">
      {/* HEADER */}
      <header className="bg-[#0f2141] text-white">
        <div className="max-w-5xl mx-auto px-4 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center">
              <Truck className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-bold">Sterile Mate · AskIsaac</div>
              <div className="text-sm font-semibold">Order Status</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-slate-400">Order #</div>
            <div className="text-sm font-mono font-bold text-white" data-testid="track-order-id">{order.id}</div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Hero */}
        <div className="mb-6">
          <div className="text-xs font-semibold text-emerald-700 uppercase tracking-widest mb-2">
            {isCancelled ? "Attention" : "Thanks — your order is in motion"}
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">
            {isCancelled
              ? `Order ${order.status === "on_hold" ? "on hold" : "cancelled"}`
              : STATUS_STEPS[currentIdx]?.label || "Processing your order"}
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            {isCancelled
              ? "Please contact Sterile Mate at 800.988.3892 for assistance."
              : STATUS_STEPS[currentIdx]?.desc}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* LEFT — timeline + line items */}
          <div className="lg:col-span-2 space-y-4">
            {/* Progress timeline */}
            {!isCancelled && (
              <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                  <Clock className="w-5 h-5 text-slate-500" />
                  <h2 className="font-semibold text-slate-900">Progress</h2>
                </div>
                <ol className="space-y-4">
                  {STATUS_STEPS.map((step, i) => {
                    const done = i <= currentIdx;
                    const active = i === currentIdx;
                    const timestamp = i === 0 ? order.approved_at
                      : step.id === "shipped" ? order.shipped_at
                      : step.id === "delivered" ? order.delivered_at
                      : null;
                    return (
                      <li key={step.id} className="flex gap-3">
                        <div className="flex flex-col items-center">
                          <div className={
                            "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 " +
                            (done ? "bg-emerald-500 text-white" : "bg-slate-200 text-slate-400")
                          }>
                            {done ? <CheckCircle2 className="w-4.5 h-4.5" /> : <span className="text-xs font-bold">{i + 1}</span>}
                          </div>
                          {i < STATUS_STEPS.length - 1 && (
                            <div className={"w-0.5 flex-1 mt-1 " + (i < currentIdx ? "bg-emerald-500" : "bg-slate-200")} style={{ minHeight: 24 }} />
                          )}
                        </div>
                        <div className="pb-4 flex-1">
                          <div className={"text-sm font-semibold " + (active ? "text-emerald-700" : done ? "text-slate-900" : "text-slate-400")}>
                            {step.label}
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5">{step.desc}</div>
                          {timestamp && (
                            <div className="text-[11px] text-slate-400 font-mono mt-1">{formatDate(timestamp)}</div>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </div>
            )}

            {/* Line items (read-only) */}
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <Package className="w-5 h-5 text-slate-500" />
                <h2 className="font-semibold text-slate-900">Line items ({items.length})</h2>
              </div>
              <div className="space-y-3">
                {items.map((it) => (
                  <div key={it.id} className="border border-slate-200 rounded-lg p-4 flex gap-4">
                    {it.image_url ? (
                      <img src={it.image_url} alt="" className="w-16 h-16 rounded object-contain bg-slate-50 flex-shrink-0" />
                    ) : (
                      <div className="w-16 h-16 rounded bg-slate-100 flex items-center justify-center flex-shrink-0">
                        <Package className="w-5 h-5 text-slate-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-900 text-sm">{it.name}</div>
                      {it.sku && <div className="text-[11px] text-slate-500 font-mono">SKU: {it.sku}</div>}
                      <div className="text-xs text-slate-600 mt-1">Qty: <strong>{it.quantity}</strong> {it.uom || "each"} · {money(it.unit_cost)} each</div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="font-bold text-slate-900" data-testid={`track-line-total-${it.id}`}>{money(it.line_total)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT — details */}
          <div className="lg:col-span-1 space-y-4">
            {/* Tracking / carrier */}
            {order.status === "shipped" || order.status === "delivered" ? (
              <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-5 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <Truck className="w-5 h-5 text-emerald-600" />
                  <h3 className="text-sm font-bold text-emerald-900 uppercase tracking-wide">
                    {order.status === "delivered" ? "Delivered" : "In transit"}
                  </h3>
                </div>
                {order.carrier && <div className="text-xs text-emerald-800 mt-1">Carrier: <strong>{order.carrier}</strong></div>}
                {order.tracking_number && (
                  <div className="text-xs text-emerald-800 mt-1">
                    Tracking:{" "}
                    {trackingLink ? (
                      <a href={trackingLink} target="_blank" rel="noreferrer" className="font-mono font-bold underline" data-testid="track-tracking-link">
                        {order.tracking_number}
                      </a>
                    ) : (
                      <span className="font-mono font-bold" data-testid="track-tracking-number">{order.tracking_number}</span>
                    )}
                  </div>
                )}
                {order.expected_delivery && (
                  <div className="text-xs text-emerald-800 mt-1">Expected: <strong>{formatDate(order.expected_delivery, false)}</strong></div>
                )}
                {order.delivered_at && (
                  <div className="text-xs text-emerald-800 mt-1">Delivered: <strong>{formatDate(order.delivered_at)}</strong></div>
                )}
              </div>
            ) : (
              order.expected_delivery && (
                <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="w-5 h-5 text-slate-500" />
                    <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500">Estimated delivery</h3>
                  </div>
                  <div className="text-sm font-semibold text-slate-900">{formatDate(order.expected_delivery, false)}</div>
                </div>
              )
            )}

            {/* Order summary */}
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Order total</h3>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between"><dt className="text-slate-600">Line items ({items.length})</dt><dd className="font-semibold text-slate-900">{money(subtotal)}</dd></div>
                <div className="flex justify-between"><dt className="text-slate-600">Freight</dt><dd className="font-semibold text-slate-900">{freight ? money(freight) : "TBD"}</dd></div>
                <div className="border-t border-slate-200 pt-2 flex justify-between text-base">
                  <dt className="font-bold text-slate-900">Total</dt>
                  <dd className="font-bold text-slate-900" data-testid="track-total">{money(total)}</dd>
                </div>
              </dl>
              {order.payment_terms && (
                <div className="mt-3 text-[11px] text-slate-500">Payment terms: <strong>{order.payment_terms}</strong></div>
              )}
              {order.customer_po_number && (
                <div className="mt-1 text-[11px] text-slate-500">PO #: <strong className="font-mono">{order.customer_po_number}</strong></div>
              )}
            </div>

            {/* Ship-to */}
            {order.shipping_address && (
              <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="w-4 h-4 text-slate-500" />
                  <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500">Ship to</h3>
                </div>
                <div className="text-sm text-slate-900 font-semibold">{order.hospital_name}</div>
                {order.department && <div className="text-xs text-slate-600">{order.department}</div>}
                <div className="text-xs text-slate-600 whitespace-pre-wrap mt-1">{order.shipping_address}</div>
              </div>
            )}

            {/* Contact + help */}
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
              <div className="text-xs text-slate-600 leading-relaxed">
                Questions about this order? Call <a href="tel:8009883892" className="font-semibold text-slate-900 hover:underline">800.988.3892</a> or reply to your confirmation email.
              </div>
            </div>
          </div>
        </div>
      </main>

      <footer className="border-t border-slate-200 bg-white mt-8">
        <div className="max-w-5xl mx-auto px-4 py-4 text-center text-[11px] text-slate-500">
          © 2026 Sterile Mate LLC · AskIsaac Inc · <a href="https://sterilemate.com" className="hover:underline">sterilemate.com</a>
        </div>
      </footer>
    </div>
  );
}

// Small helpers used only by the tracking view -------------------------------
function carrierTrackingLink(carrier, trackingNumber) {
  if (!carrier || !trackingNumber) return null;
  const c = carrier.toLowerCase();
  const t = encodeURIComponent(trackingNumber.trim());
  if (c.includes("fedex")) return `https://www.fedex.com/fedextrack/?trknbr=${t}`;
  if (c.includes("ups")) return `https://www.ups.com/track?tracknum=${t}`;
  if (c.includes("usps")) return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${t}`;
  if (c.includes("dhl")) return `https://www.dhl.com/en/express/tracking.html?AWB=${t}`;
  return null;
}

function formatDate(iso, includeTime = true) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    const opts = includeTime
      ? { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }
      : { month: "long", day: "numeric", year: "numeric" };
    return d.toLocaleDateString("en-US", opts);
  } catch {
    return String(iso);
  }
}

